import React from 'react';
import { Box, Paper, Popper, Skeleton, Typography } from '@mui/material';
import { AreaChart, Area } from 'recharts';
import api from '../services/api';

const SEVERITY_COLORS = {
  1: '#97AAB3',
  2: '#7499FF',
  3: '#FFC859',
  4: '#FFA059',
  5: '#E45959',
};

function formatDuration(clockStr) {
  const diff = Date.now() - new Date(clockStr).getTime();
  const h = Math.floor(diff / 3600000);
  const m = Math.floor((diff % 3600000) / 60000);
  if (h > 24) return `há ${Math.floor(h / 24)}d${h % 24}h`;
  if (h > 0) return `há ${h}h${m}m`;
  return `há ${m}m`;
}

const sparklineCache = {};

function AlarmSparkline({ itemid, cod_empresa, color }) {
  const [data, setData] = React.useState(null);

  React.useEffect(() => {
    if (!itemid || itemid === '0' || itemid === 0) return;
    const key = String(itemid);
    if (sparklineCache[key]) {
      setData(sparklineCache[key]);
      return;
    }

    api.post('map/items-data', { cod_empresa, itemid: [key] })
      .then(res => {
        const now = Math.floor(Date.now() / 1000);
        const points = (res.data || [])
          .filter(p => Number(p.clock) >= now - 1800)
          .map(p => ({ v: Number(p.value), t: Number(p.clock) }))
          .sort((a, b) => a.t - b.t);
        sparklineCache[key] = points;
        setData(points);
      })
      .catch(() => setData([]));
  }, [itemid, cod_empresa]);

  if (!itemid || itemid === '0' || itemid === 0) return null;
  if (data === null) return <Skeleton variant="rectangular" width={180} height={40} sx={{ borderRadius: 0.5 }} />;
  if (data.length === 0) return null;

  return (
    <AreaChart width={180} height={40} data={data}>
      <Area type="monotone" dataKey="v" stroke={color} fill={color}
        fillOpacity={0.15} strokeWidth={1.5} dot={false} isAnimationActive={false} />
    </AreaChart>
  );
}

export default function HostHoverCard({ open, anchorEl, data, problems, cod_empresa, onMouseEnter, onMouseLeave }) {
  if (!open) return null;

  const hasProblems = problems && problems.length > 0;
  const isOffline = problems?.some(p => p.name === 'Indisponível por PING');

  return (
    <Popper open={open} anchorEl={anchorEl} placement="right-start"
      modifiers={[{ name: 'offset', options: { offset: [0, 8] } }]}
      style={{ zIndex: 1500 }}
    >
      <Paper
        elevation={8}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        sx={{
          width: 300,
          maxHeight: 400,
          overflow: 'auto',
          p: 1.5,
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'divider',
        }}
      >
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
            <Box sx={{
              width: 8, height: 8, borderRadius: '50%',
              backgroundColor: isOffline ? '#E45959' : '#4caf50',
              boxShadow: isOffline
                ? '0 0 6px #E45959'
                : '0 0 6px #4caf50',
            }} />
            <Typography variant="subtitle2" fontWeight="bold" noWrap>
              {data.label}
            </Typography>
          </Box>
          <Typography variant="caption" sx={{
            color: isOffline ? '#E45959' : '#4caf50',
            fontWeight: 600,
          }}>
            {isOffline ? 'Offline' : 'Online'}
          </Typography>
        </Box>

        {/* IP + Model */}
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          {[data.ip, data.model].filter(Boolean).join('  •  ')}
        </Typography>

        {/* Problems */}
        {!hasProblems ? (
          <Typography variant="caption" color="text.secondary" sx={{ opacity: 0.6 }}>
            Sem alarmes ativos
          </Typography>
        ) : (
          problems.map((p, i) => {
            const color = SEVERITY_COLORS[p.severity] || '#97AAB3';
            return (
              <Box key={p.event_id || i} sx={{
                mb: i < problems.length - 1 ? 1 : 0,
                py: 0.5,
                borderTop: i > 0 ? '1px solid' : 'none',
                borderColor: 'divider',
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0, flex: 1 }}>
                    <Box sx={{
                      width: 6, height: 6, borderRadius: '50%',
                      backgroundColor: color, flexShrink: 0,
                    }} />
                    <Typography variant="caption" fontWeight={600} noWrap sx={{ flex: 1 }}>
                      {p.name}
                    </Typography>
                  </Box>
                  <Typography variant="caption" color="text.secondary" sx={{ ml: 1, flexShrink: 0, fontSize: 10 }}>
                    {p.clock ? formatDuration(p.clock) : ''}
                  </Typography>
                </Box>
                <AlarmSparkline itemid={p.itemid} cod_empresa={cod_empresa} color={color} />
              </Box>
            );
          })
        )}
      </Paper>
    </Popper>
  );
}
